import { Dialog, InlineDialog } from 'components/dialogs/Dialog'
import { observer } from 'mobx-react-lite'
import { DialogCloseButton } from 'components/dialogs/DialogCloseButton'
import { useCubitBuilder } from 'hooks/cubits'
import { PreviewMeetingCubit } from '@breakoutlearning/firebase-repository/cubits/PreviewMeetingCubit'
import { useBreakoutUser } from 'hooks/profile'
import { useRepository } from 'hooks/auth'
import { MeetingResultsSlide } from '@breakoutlearning/firebase-repository/cubits/meeting/slides/MeetingResultsSlide'
import { SlideModel } from '@breakoutlearning/firebase-repository/models/SlideModel'
import { Spinner } from 'components/Spinner'
import type { RoomStateAnswer } from '@breakoutlearning/firebase-repository/models/RoomStateAnswer'
import type { RoomState } from '@breakoutlearning/firebase-repository/models/RoomState'
import type { RoomStateEngagement } from '@breakoutlearning/firebase-repository/models/RoomStateEngagement'
import type { RoomStateRubricResultDetail } from '@breakoutlearning/firebase-repository/models/RoomStateRubricResultDetail'
import type { RoomStateRubricResult } from '@breakoutlearning/firebase-repository/models/RoomStateRubricResult'
import { lazy, Suspense } from 'react'
import type { SlideRubric } from '@breakoutlearning/firebase-repository/models/SlideRubric'
import { useTranslationTyped } from 'i18n/i18n'
import { ChevronLeft } from 'components/icons/ChevronLeft'

// Eagerly load the ConversationMapAnimatedGraphBucketed component
// It's used in MeetingResultsSlideView2, but it's lazy load might happen later,
// which means that if we deploy a new version of the app, the component might not be available
// and will cause a page reload
// Be doing this, we ensure that the component is available when needed and no reload is needed
import { type InstructorAssignmentCubit } from '@breakoutlearning/firebase-repository/cubits/InstructorAssignmentCubit'
;(async () => {
  await import('components/MeetingResultsSlideView2/ConversationMapAnimatedGraphBucketed')
})().catch(() => {
  // Safe to ignore: the vite:preloadError handler already reported a
  // signed-in user's failure, and if the chunk is still missing when the map
  // renders, its lazy() import throws into an error boundary that offers a
  // reload. Uncaught, the rejection reached Sentry a second time and bypassed
  // the handler's logged-out filter, so email link scanners that close the
  // page mid-fetch filled CASES-REACT-17X.
})

const MeetingResultsSlideView2 = lazy(
  async () =>
    await import('components/MeetingResultsSlideView2/MeetingResultsSlideView2')
)

type MeetingResultsDialogNavigationProps = {
  inline?: boolean
  onBack?: () => void
  onDismiss?: () => void
}

type MeetingResultsDialogProps = MeetingResultsDialogNavigationProps &
  (
    | {
        roomId: string
        targetUserId?: string
        roomState?: never
        dataForRoom?: never
        instructorAssignmentCubit?: never
        onStudentAssignmentPage?: boolean
      }
    | {
        roomState: RoomState
        instructorAssignmentCubit: InstructorAssignmentCubit
        targetUserId?: string
        roomId?: never
        dataForRoom?: never
        onStudentAssignmentPage?: boolean
      }
    | {
        roomState: RoomState
        dataForRoom: {
          roomStateAnswersForGroup: Array<RoomStateAnswer>
          roomStateAnswersPerUser: Map<string, Array<RoomStateAnswer>>
          roomStateEngagement: Array<RoomStateEngagement>
          rubrics: Array<SlideRubric>
          rubricResults: Array<RoomStateRubricResult>
          rubricResultDetails: Array<RoomStateRubricResultDetail>
        }
        targetUserId?: string
        instructorAssignmentCubit?: never
        roomId?: never
        onStudentAssignmentPage?: boolean
      }
  )

export const MeetingResultsDialog = observer(function MeetingResultsDialog({
  roomId,
  roomState,
  targetUserId,
  dataForRoom,
  instructorAssignmentCubit: cubit,
  onStudentAssignmentPage,
  inline = false,
  onBack,
  onDismiss,
}: MeetingResultsDialogProps) {
  const user = useBreakoutUser()
  const repository = useRepository()
  const DialogToUse = inline ? InlineDialog : Dialog

  // init cubits
  const meetingCubit = useCubitBuilder(() => {
    // let cubit handle data fetch
    if (roomId !== undefined) {
      return new PreviewMeetingCubit({
        roomId,
        repository,
        currentUser: user,
        enableTalkTrack: false,
      })
    }

    // seed data provided, pass to cubit
    if (dataForRoom)
      return new PreviewMeetingCubit({
        roomId,
        slideDeckId: roomState.slideDeckId,
        repository,
        currentUser: user,
        seedData: {
          roomStateAnswersForGroup: dataForRoom.roomStateAnswersForGroup,
          roomStateAnswersPerUser: dataForRoom.roomStateAnswersPerUser,
          roomState,
          users: roomState.users,
        },
        enableTalkTrack: false,
      })

    // cubit provided, generate seed data
    const roomAnswers = cubit.roomStateAnswers.models.filter(
      (a) => a.data.roomId === roomState.id
    )
    const roomStateAnswersPerUser = roomAnswers.reduce((acc, answer) => {
      const { userId } = answer.data
      if (!userId) return acc
      if (!acc.has(userId)) {
        acc.set(userId, [])
      }
      acc.get(userId)?.push(answer)
      return acc
    }, new Map<string, RoomStateAnswer[]>())

    const roomStateAnswersForGroup =
      roomStateAnswersPerUser.get(roomState.id) ?? []

    return new PreviewMeetingCubit({
      roomId,
      slideDeckId: roomState.slideDeckId,
      repository,
      currentUser: user,
      seedData: {
        roomStateAnswersForGroup,
        roomStateAnswersPerUser,
        roomState,
        users: roomState.users,
      },
      enableTalkTrack: false,
    })
  }, [repository, user, roomId])

  const dataReady =
    meetingCubit.roomState.isLoaded && meetingCubit.slideDeck.isLoaded

  // create a mock slide
  const resultsSlide = new MeetingResultsSlide(
    meetingCubit,
    SlideModel.empty(repository),
    0
  )

  const { tt } = useTranslationTyped()

  return (
    <DialogToUse
      size="xl"
      className="flex flex-col justify-start overflow-hidden bg-core-secondary"
      innerClassName="p-0! flex"
      onDismiss={onDismiss}
      forceBackdrop={inline ? false : undefined}
      aria-labelledby={undefined}
      aria-label={tt.meeting.results.breakout_session_results()}
    >
      {onBack ? (
        <button
          type="button"
          className="focus-ring-floating absolute top-4 left-4 z-50 flex items-center gap-1 rounded-lg p-1 text-label-medium text-on-surface-var hover:text-on-surface"
          data-testid="meeting-results-back-button"
          onClick={onBack}
        >
          <ChevronLeft size={18} />
          {tt.common.back()}
        </button>
      ) : null}
      <DialogCloseButton
        className="absolute top-0 right-0 pb-0"
        onClick={() => {
          onDismiss?.()
          return !inline
        }}
      />
      {dataReady ? (
        <Suspense fallback={<NotReadyView />}>
          <div className="h-full overflow-auto">
            <MeetingResultsSlideView2
              slide={resultsSlide}
              inDialog={true}
              targetUserId={targetUserId}
              onStudentAssignmentPage={onStudentAssignmentPage}
              seedData={
                dataForRoom ??
                getResultsSlideSeedDataFromInstructorAssignmentCubit({
                  roomId: roomId ?? roomState.id,
                  cubit,
                })
              }
            />
          </div>
        </Suspense>
      ) : (
        <NotReadyView />
      )}
    </DialogToUse>
  )
})

const NotReadyView = () => (
  <div className="flex w-full grow items-center justify-center">
    <Spinner />
  </div>
)

const getResultsSlideSeedDataFromInstructorAssignmentCubit = ({
  cubit,
  roomId,
}: {
  cubit: InstructorAssignmentCubit | undefined
  roomId: string
}) => {
  if (!cubit) return undefined
  return {
    roomStateEngagement: cubit.roomStateEngagements.models.filter(
      (engagement) => engagement.data.roomId === roomId
    ),
    rubricResultDetails: cubit.roomStateRubricResultDetails.models.filter(
      (resultDetail) => resultDetail.data.roomId === roomId
    ),
    rubricResults: cubit.roomStateRubricResults.models.filter(
      (r) => r.data.roomId === roomId
    ),
    rubrics: Array.from(cubit.rubrics.keys()),
  }
}
